/**
 * Logika gambar-per-frame panggung WebGL: resize kanvas ke viewport, lalu
 * gambar tiap instance terdaftar sebagai sprite (viewport+scissor ke posisi
 * elemen aslinya). Dipisah dari orb-webgl-stage.ts (yang memegang siklus
 * hidup konteks) supaya kedua berkas tetap di bawah batas ukuran.
 */

import { ORB_MORPH_SPRITE_SCALE } from "@/components/assistant/orb/orb.config";
import { pixelShift } from "@/components/assistant/orb/orb-motion";
import { scaleRectAroundCenter } from "./orb-morph-motion";
import {
	type SpriteGeometry,
	setGlassUniforms,
	setMorphUniforms,
} from "./orb-webgl-draw-uniforms";
import type { ProgramInfo } from "./orb-webgl-programs";
import type {
	WebglOrbInstanceState,
	WebglShaderKind,
} from "./orb-webgl-stage.types";

export interface DrawContext {
	gl: WebGL2RenderingContext;
	canvas: HTMLCanvasElement;
	programs: ReadonlyMap<WebglShaderKind, ProgramInfo>;
	instances: ReadonlySet<() => WebglOrbInstanceState>;
}

/** Ukuran kanvas overlay mengikuti viewport, bukan seluruh halaman (sprite diposisikan via getBoundingClientRect per frame). */
function resizeToViewport(canvas: HTMLCanvasElement, dpr: number): void {
	const width = Math.max(1, Math.round(window.innerWidth * dpr));
	const height = Math.max(1, Math.round(window.innerHeight * dpr));
	if (canvas.width !== width || canvas.height !== height) {
		canvas.width = width;
		canvas.height = height;
	}
}

function drawInstance(
	ctx: DrawContext,
	getState: () => WebglOrbInstanceState,
	bufferWidth: number,
	bufferHeight: number,
	dpr: number,
	elapsedMs: number,
): void {
	const { gl } = ctx;
	const state = getState();
	const info = ctx.programs.get(state.kind);
	const placeholder = state.getRect();
	if (!info || !placeholder) return;
	if (placeholder.width <= 0 || placeholder.height <= 0) return;
	// liquid-morph mengisi sebagian kanvasnya: sprite lebih besar dari placeholder.
	const rect =
		state.kind === "liquid-morph"
			? scaleRectAroundCenter(placeholder, ORB_MORPH_SPRITE_SCALE)
			: placeholder;

	const still = !state.animate || state.frozen;
	// Anti burn-in: pergeseran halus posisi berlaku untuk SEMUA jenis shader.
	const shift = still ? { x: 0, y: 0 } : pixelShift(elapsedMs);
	const vw = Math.round(rect.width * dpr);
	const vh = Math.round(rect.height * dpr);
	const vx = Math.round((rect.x + shift.x) * dpr);
	// Flip ke origin kiri-bawah GL; rect.y dari getBoundingClientRect() sudah
	// dalam ruang viewport yang sama dengan kanvas overlay (keduanya fixed).
	const vy = Math.round(bufferHeight - (rect.y + shift.y) * dpr - vh);
	// Di luar layar (scroll jauh atau belum terlihat) — lewati, hemat kerja.
	if (vx + vw < 0 || vx > bufferWidth || vy + vh < 0 || vy > bufferHeight) {
		return;
	}

	// biome-ignore lint/correctness/useHookAtTopLevel: gl.useProgram() adalah metode WebGL2RenderingContext asli, bukan React hook — namanya kebetulan cocok pola use*
	gl.useProgram(info.program);
	gl.viewport(vx, vy, vw, vh);
	gl.scissor(vx, vy, vw, vh);
	const geo: SpriteGeometry = { x: vx, y: vy, width: vw, height: vh };
	if (info.kind === "liquid-morph" && state.kind === "liquid-morph") {
		setMorphUniforms(gl, info.locations, state, geo, elapsedMs, still);
	} else if (info.kind === "liquid-glass" && state.kind === "liquid-glass") {
		setGlassUniforms(gl, info.locations, state, geo, elapsedMs, still);
	} else {
		return; // jenis state & program tak cocok — tak seharusnya terjadi
	}
	gl.drawArrays(gl.TRIANGLES, 0, 3);
}

/** Satu frame: bersihkan kanvas, lalu gambar semua instance terdaftar sebagai sprite. */
export function drawWebglStageFrame(ctx: DrawContext, elapsedMs: number): void {
	const { gl, canvas } = ctx;
	const dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
	resizeToViewport(canvas, dpr);
	const { width, height } = canvas;
	gl.scissor(0, 0, width, height); // scissor memengaruhi clear(); sapu seluruh kanvas dulu
	gl.clear(gl.COLOR_BUFFER_BIT);
	for (const getState of ctx.instances) {
		drawInstance(ctx, getState, width, height, dpr, elapsedMs);
	}
}
